import { MemberListService } from 'src/app/core/service/grid-data/grid-data-member.service';
import { FHIR_DEFAULT_PAGE_URL, FhirService } from '../../core/service/fhir.service';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { State } from '@progress/kendo-data-query';
import { SettingService } from 'src/app/core/service/setting.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UserService } from 'src/app/core/service/user.service';
import { Subscription } from 'rxjs';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { ReferralComponent } from 'src/app/referral/referral.component';
import { MessageService } from 'src/app/core/service/message.service';
import { SelectEvent } from '@progress/kendo-angular-layout/tabstrip/events';
import { Router } from '@angular/router';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { MemberService } from 'src/app/core/service/member.service';
import { PushEventService } from 'src/app/core/service/push-event.service';

@Component({
  standalone: false,
  selector: 'app-fhir-summary',
  templateUrl: './fhir-summary.component.html',
  styleUrls: ['./fhir-summary.component.scss']
})
export class FhirSummaryComponent implements OnInit, OnDestroy
{
  @ViewChild('referral') public referral: ReferralComponent;

  public loaded = false;
  public member = null;
  public patientContext = null;
  public patientLabel = {};
  public patientLabelString: string;
  public referralsEnabled = false;
  public communityEnabled = false;
  public referralsCollapsed = false;
  public ehrShowAssessments = false;
  public multiplePatientsFound = false;
  public messagesEnabled = false;
  public notesEnabled = false;
  public unreadMessages = 0;
  public messageType = 'inbox';
  public selectedTab = 0;
  public subjectIdentifierMPI = null;
  public tabCounts: any;
  public clientRegistrationRequestSent = false;
  public clientFound = false;

  private readonly dictionaryService = inject(DictionaryService);
  private readonly router = inject(Router);
  private readonly fhirService = inject(FhirService);
  private readonly settingService = inject(SettingService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  private readonly messengerService = inject(MessengerService);
  private readonly messageService = inject(MessageService);
  private readonly messageHandlerService = inject(MessageHandlerService);
  private readonly pushEventService = inject(PushEventService);
  private readonly memberService = inject(MemberService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly memberListService = inject(MemberListService);

  private messagingSubscription: Subscription = this.messengerService.receiveMessage().subscribe({
    next: (message) =>
    {
      if (message.type === 'getCaseDetailsData')
      {
        this.refreshCases(2500);
      }
      else if (message && message.type && message.type === 'wsMessagesCount')
      {
        const responseMessage = JSON.parse(message.data.body);
        this.unreadMessages = responseMessage.unreadCount;
        this.changeDetectorRef.detectChanges();
      }
    }
  });

  public get patientMPI(): string
  {
    return this.patientContext && (this.patientContext?.mpi ? this.patientContext?.mpi : null);
  }

  public get patientName(): string
  {
    return this.patientContext && (this.patientContext?.firstName || this.patientContext?.lastName) ?
      `${this.patientContext?.firstName} ${this.patientContext?.lastName}` : '';
  }

  public get patientLastName(): string
  {
    return this.patientContext && (this.patientContext?.lastName ? this.patientContext?.lastName : null);
  }

  public get patientFirstName(): string
  {
    return this.patientContext && (this.patientContext?.firstName ? this.patientContext?.firstName : null);
  }  

  public get patientDOB(): string
  {
    return this.patientContext ? this.patientContext?.dob : null;
  }

  /**
   * Provide loading property indicators for list.
   */
  public get loadingSubjects(): boolean
  {
    return this.memberListService.loading;
  }

  public ngOnInit(): void
  {
    this.dictionaryService.getCachedIdentifiers().subscribe({
      next: (data) =>
      {
        this.subjectIdentifierMPI = data.find(x => x.code.toLowerCase() === 'mpi');
        this.settingService.getCachedSessionSettings().subscribe({
          next: (sessionSetting) =>
          {
            this.ehrShowAssessments = sessionSetting.ehrShowAssessments;
            this.communityEnabled = sessionSetting.communityEnabled;
            this.patientLabel = { patientLabel: sessionSetting.patientLabel };
            this.patientLabelString = sessionSetting.patientLabel;
            this.patientContext = this.fhirService.patientContext;
            this.referralsEnabled = sessionSetting.communityEnabled && ((sessionSetting.referralsRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.referralsRoles) : false);
            this.messagesEnabled = (sessionSetting.messagesRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.messagesRoles) : false;
            this.notesEnabled = (sessionSetting.notesRoles !== '') ?
              this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.notesRoles) : false;

            this.loadPatient();
          }
        });
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public referralsOpenStateChanged(e: boolean): void
  {
    this.referralsCollapsed = e;
  }

  public onRegisterClient(): void
  {  
    const data: any = {
      pushType: 'PATIENT_REGISTRATION',
      lastName: this.patientLastName,
      firstName: this.patientFirstName,
      dateOfBirth: this.patientDOB,//?.format('YYYY-MM-DD'),
      mpi: this.patientMPI
    };
    this.pushEventService.createPushEvent(data).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CLIENT-REGISTRATION-SUCCESSFUL'.replace('{{patientLabel}}', this.patientLabelString));
        this.clientRegistrationRequestSent = true;
      }
    });
    
  }

  public newReferral(): void
  {
    this.referral.newReferral(this.ehrShowAssessments);
  }

  public backToSummary(): void
  {
    const patientId = this.fhirService.patientIdContext;
    this.fhirService.clearStorage(true);
    this.router.navigateByUrl(`/patient/${patientId}`);
  }

  /**
   * Get count of unread messages for the current user.
   */
  public getCountMessages(): void
  {
    if (this.messagesEnabled)
    {
      const unread = 0;
      if (this.authenticationService.isAuthenticated)
      {
        const user = this.authenticationService.currentUser;
        if (user.role !== 'ROLE_COMMUNITY_ADMIN' && user.role !== 'ROLE_SYSTEM_OPERATOR')
        {
          this.messageService.getCountMessageByUserAndStatus(this.authenticationService.currentUser.userName, unread).subscribe({
            next: (data) =>
            {
              this.unreadMessages = data;
            },
            error: (errorResponse: any) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      }
    }
  }

  public onTabSelect(e: SelectEvent): void
  {
    this.selectedTab = e.index;
    if (this.selectedTab === 1)
    {
      this.messageType = 'inbox';
      this.router.navigateByUrl(`${FHIR_DEFAULT_PAGE_URL}/messages/${this.messageType}`);
    }
  }

  public getMemberCountTabs(): void
  {
    if (this.notesEnabled && this.member !== null)
    {
      this.memberService.getMemberCountTabs(this.member.id).subscribe({
        next: (data) =>
        {
          this.tabCounts = data;
        },
        error: (errorResponse: any) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  private loadPatient(): void
  {
    this.messageType = 'inbox';
    this.multiplePatientsFound = false;

    const state: State = { skip: 0, take: 5, filter: { logic: 'and', filters: [] } };

    if (this.patientMPI)
    {
      if (!this.subjectIdentifierMPI) 
      {
        this.messageHandlerService.displayError('MPI-DICTIONARY-NOT-FOUND');
        this.loaded = true;
        return;
      }
      else
      {
        state.filter.filters.push({ field: 'identifierDictionary', operator: 'eq', value: this.subjectIdentifierMPI.id, logic: 'and' });
        state.filter.filters.push({ field: 'identifier', operator: 'eq', value: this.patientMPI, logic: 'and' });
      }
    }
    else if (this.fhirService.patientIdContext != null)
    {
      state.filter.filters.push({ field: 'id', operator: 'eq', value: this.fhirService.patientIdContext });
    }
    else if (this.patientName)
    {
      state.filter.filters.push({ field: 'fullName', operator: 'eq', value: this.patientName });
      if (this.patientDOB)
      {
        state.filter.filters.push({ field: 'dateOfBirth', operator: 'eq', value: this.patientDOB });
      }
    }
    else
    {
      this.loaded = true;
      return;
    }

    this.memberListService.queryByState(state, (patient: any) =>
    {
      if (patient !== null && patient.data !== null)
      {
        patient.data.forEach(v =>
        {
          if (v.dateOfBirth !== null && v.dateOfBirth !== undefined && v.dateOfBirth.length === 10)
          {
            v.dateOfBirth = new Date(`${v.dateOfBirth}T12:00:00.000`);
          }
        });

        if (patient.total === 1)
        {
          this.member = patient.data[0];
          this.fhirService.saveChannelsPatient(this.member);
          this.patientContext = this.fhirService.patientContext;
          this.clientFound = true;
        }
        else if (patient.total > 1)
        {
          this.multiplePatientsFound = true;    // Could eventually be extended to add a selector.
        }
        else if (patient.total === 0)
        {
          this.pushEventService.pushEventForPatientExists(this.patientLastName, this.patientFirstName, this.patientMPI, this.patientDOB).subscribe({
            next: (response) =>
            {
                if (response)
                {
                  this.clientRegistrationRequestSent = true;
                }
                else
                {
                  this.clientRegistrationRequestSent = false;
                }
            }
        });
        }
        this.getCountMessages();
        this.getMemberCountTabs();
        this.loaded = true;
      }
    });
  }

  private refreshCases(delayTime: number): void
  {
    setTimeout(() =>
    {
      this.messengerService.sendMessage(new MessageSubject('reloadCaseListDetailComponent'));
    }, delayTime);
  }
}
